import React, { useState, useRef } from 'react'
import { Upload, X, Image as ImageIcon, Copy, Check, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { uploadToCloudinary, isCloudinaryConfigured } from '../../config/cloudinary'

const ImageUploader = ({ onUploadComplete }) => {
  const [dragActive, setDragActive] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [preview, setPreview] = useState(null)
  const [uploadedImage, setUploadedImage] = useState(null)
  const [copied, setCopied] = useState(false)
  const fileInputRef = useRef(null)

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  const handleChange = (e) => {
    e.preventDefault()
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const handleFile = (file) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WebP images.')
      return
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      toast.error('File size exceeds 10MB limit.')
      return
    }

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreview({
        url: reader.result,
        name: file.name,
        size: (file.size / 1024).toFixed(2) + ' KB',
        type: file.type,
      })
    }
    reader.readAsDataURL(file)

    // Upload to Cloudinary
    uploadImage(file)
  }

  const uploadImage = async (file) => {
    if (!isCloudinaryConfigured()) {
      toast.error(
        <div>
          <p className="font-semibold mb-2">Cloudinary Not Configured</p>
          <p className="text-sm">Please set up Cloudinary credentials:</p>
          <ol className="text-sm mt-2 ml-4 list-decimal">
            <li>Get credentials from <a href="https://console.cloudinary.com/settings/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Cloudinary Console</a></li>
            <li>Add to .env file:
              <pre className="bg-gray-100 p-2 rounded mt-1 text-xs">
                VITE_CLOUDINARY_CLOUD_NAME=your_cloud_name{'\n'}
                VITE_CLOUDINARY_UPLOAD_PRESET=your_preset
              </pre>
            </li>
            <li>Restart development server</li>
          </ol>
        </div>,
        { duration: 10000 }
      )
      return
    }

    setUploading(true)
    setUploadProgress(0)

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval)
            return 90
          }
          return prev + 10
        })
      }, 200)

      const result = await uploadToCloudinary(file)
      
      clearInterval(progressInterval)
      setUploadProgress(100)

      setUploadedImage(result)
      toast.success('Image uploaded successfully!')

      if (onUploadComplete) {
        onUploadComplete(result)
      }
    } catch (error) {
      console.error('Upload error:', error)
      toast.error('Upload failed. Please try again.')
      setPreview(null)
    } finally {
      setUploading(false)
    }
  }

  const copyToClipboard = () => {
    if (uploadedImage?.url) {
      navigator.clipboard.writeText(uploadedImage.url)
      setCopied(true)
      toast.success('URL copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const resetUpload = () => {
    setPreview(null)
    setUploadedImage(null)
    setUploadProgress(0)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">Image Upload</h3>
        {uploadedImage && (
          <button
            onClick={resetUpload}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Upload Another
          </button>
        )}
      </div>

      {!uploadedImage ? (
        <>
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              dragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleChange}
            />

            {!preview ? (
              <div className="space-y-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <Upload className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900 mb-2">
                    Drag and drop your image here
                  </p>
                  <p className="text-sm text-gray-600 mb-4">or</p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Browse Files
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Supported formats: JPEG, PNG, GIF, WebP (Max 10MB)
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative inline-block">
                  <img
                    src={preview.url}
                    alt="Preview"
                    className="max-h-64 rounded-lg shadow-md"
                  />
                  {!uploading && (
                    <button
                      onClick={resetUpload}
                      className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  <p className="font-medium">{preview.name}</p>
                  <p>{preview.size}</p>
                </div>
                {uploading && (
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-600">
                      Uploading... {uploadProgress}%
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {!isCloudinaryConfigured() && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Cloudinary Setup Required</p>
                <p>
                  Get your credentials from{' '}
                  <a
                    href="https://console.cloudinary.com/settings/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-900 underline font-medium"
                  >
                    Cloudinary Console
                  </a>{' '}
                  and add them to your .env file.
                </p>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-6">
          <div className="relative">
            <img
              src={uploadedImage.url}
              alt="Uploaded"
              className="w-full rounded-lg shadow-md"
            />
            <div className="absolute top-2 right-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
              <Check className="w-4 h-4" />
              Uploaded
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image URL
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={uploadedImage.url}
                  readOnly
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                />
                <button
                  onClick={copyToClipboard}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Dimensions</p>
                <p className="font-semibold text-gray-900">
                  {uploadedImage.width} × {uploadedImage.height}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Format</p>
                <p className="font-semibold text-gray-900 uppercase">
                  {uploadedImage.format}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Public ID</p>
                <p className="font-semibold text-gray-900 truncate">
                  {uploadedImage.publicId}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ImageUploader